import { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { Property } from '../types';
import { useConnection } from '../context/ConnectionContext';
import { searchPropertiesApi, searchByCodesApi } from '../services/propertyApiClient';
import { buildBackendFilterPayload, BrokerFilterHookInput } from './propertyFilterPayloadBuilder';

export const PROPERTIES_PER_PAGE = 20;

export const useBrokerPropertiesData = (input: BrokerFilterHookInput & { spatialFilter?: any }) => {
  const { propertyCodeFilter = '', searchMode = 'ai' } = input;
  const [highlights, setHighlights] = useState<Property[]>([]);
  const [regularItems, setRegularItems] = useState<Property[]>([]);
  const [totalMatchingPropertiesCount, setTotalMatchingPropertiesCount] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [currentPage, setCurrentPage] = useState(1);
  const [isLoadingProperties, setIsLoadingProperties] = useState(false);
  const [fetchPropertiesError, setFetchPropertiesError] = useState<string | null>(null);

  const activeRequestIdRef = useRef<number>(0);
  const { setIsOffline } = useConnection();

  const filterSignature = useMemo(() => JSON.stringify({
    code: input.propertyCodeFilter || '',
    ubicacion: input.ubicacionFilter || [],
    types: input.propertyTypeFilter || [],
    estrategias: input.estrategiaComercialFilter || [],
    owner: input.ownerFilter || [],
    rooms: input.roomsFilter || [],
    baths: input.bathsFilter || [],
    garages: input.garagesFilter || [],
    price: input.priceFilter || null,
    area: input.areaFilter || null,
    estrato: input.estratoFilter || [],
    elevator: !!input.withElevatorFilter,
    recent: !!input.onlyRecentFilter,
    spatial: input.spatialFilter || null,
    mode: input.searchMode || 'ai',
    sort: input.sortConfigs || [],
    userRole: input.appUser?.role,
  }), [
    input.propertyCodeFilter, input.ubicacionFilter, input.propertyTypeFilter,
    input.estrategiaComercialFilter, input.ownerFilter, input.roomsFilter,
    input.bathsFilter, input.garagesFilter, input.priceFilter, input.areaFilter,
    input.estratoFilter, input.withElevatorFilter, input.onlyRecentFilter,
    input.spatialFilter, input.searchMode, input.sortConfigs, input.appUser?.role,
  ]);

  const prevSignatureRef = useRef(filterSignature);
  useEffect(() => {
    if (prevSignatureRef.current !== filterSignature) {
      prevSignatureRef.current = filterSignature;
      setCurrentPage(1);
    }
  }, [filterSignature]);

  const fetchProperties = useCallback(async () => {
    const reqId = ++activeRequestIdRef.current;
    if (searchMode === 'code' && !propertyCodeFilter.trim()) {
      setHighlights([]);
      setRegularItems([]);
      setTotalMatchingPropertiesCount(0);
      setTotalPages(1);
      setIsLoadingProperties(false);
      return;
    }

    setIsLoadingProperties(true);
    setFetchPropertiesError(null);

    try {
      if (searchMode === 'code') {
        const result = await searchByCodesApi(propertyCodeFilter);
        if (reqId === activeRequestIdRef.current) {
          setHighlights([]);
          setRegularItems(result.items || []);
          setTotalMatchingPropertiesCount(result.total || 0);
          setTotalPages(Math.ceil((result.total || 0) / PROPERTIES_PER_PAGE) || 1);
          setIsOffline(false);
        }
      } else {
        const payload = buildBackendFilterPayload(input, currentPage, PROPERTIES_PER_PAGE);
        const result = await searchPropertiesApi(payload);
        if (reqId === activeRequestIdRef.current) {
          setHighlights(result.highlights || []);
          setRegularItems(result.items || []);
          setTotalMatchingPropertiesCount(result.total || 0);
          setTotalPages(result.totalPages || 1);
          setIsOffline(false);
        }
      }
    } catch (err: any) {
      if (reqId === activeRequestIdRef.current) {
        console.error('[useBrokerPropertiesData] API Error:', err);
        setFetchPropertiesError(err?.message || 'Error cargando inmuebles.');
      }
    } finally {
      if (reqId === activeRequestIdRef.current) {
        setIsLoadingProperties(false);
      }
    }
  }, [filterSignature, currentPage, searchMode, propertyCodeFilter, input, setIsOffline]);

  useEffect(() => {
    fetchProperties();
  }, [fetchProperties]);

  const allCurrentProperties = useMemo(() => {
    return currentPage === 1 ? [...highlights, ...regularItems] : regularItems;
  }, [currentPage, highlights, regularItems]);

  return {
    properties: allCurrentProperties,
    highlights,
    allMatchingProperties: allCurrentProperties,
    isLoadingProperties,
    fetchPropertiesError,
    currentPage,
    totalPages,
    totalMatchingPropertiesCount,
    handlePageChange: (p: number) => {
      if (p !== currentPage && p >= 1 && p <= totalPages) setCurrentPage(p);
    },
    updatePropertyLocally: (id: string, update: Partial<Property>) => {
      setRegularItems((prev) => prev.map((x) => (x.id === id ? { ...x, ...update } : x)));
      setHighlights((prev) => prev.map((x) => (x.id === id ? { ...x, ...update } : x)));
    },
    fetchAllMatchingProperties: async () => allCurrentProperties,
  };
};


